import React, { useState, useEffect, useMemo } from 'react';
import { Terminal, Cpu, Globe, ChevronRight } from 'lucide-react';

// Types - must match Home.tsx ActiveOverlay type
type ActiveOverlay = 'none' | 'ecosystem' | 'terminal' | 'gallery';

interface LandingPageProps {
  setActiveOverlay: React.Dispatch<React.SetStateAction<ActiveOverlay>>;
}

// -----------------------------------------------------------------------------
// DATA CONSTANTS
// -----------------------------------------------------------------------------

const BRILLIANT_MOMENTS = [
  { id: 'topology', title: 'THE TOPOLOGY PALACE', category: 'META-SYSTEMS', tier: 'APEX', brilliance: 98, color: '#0891b2', x: 50, y: 10 },
  { id: 'compiler', title: 'COMPILER CONSTRUCTION', category: 'THEORETICAL CS', tier: 'CORE', brilliance: 97, color: '#c026d3', x: 15, y: 25 },
  { id: 'vhdl', title: 'VHDL NEURAL NETWORK', category: 'HARDWARE-ML', tier: 'CORE', brilliance: 95, color: '#059669', x: 85, y: 25 },
  { id: 'purdue', title: 'MS ECE PURDUE', category: 'ACADEMIC', tier: 'APEX', brilliance: 90, color: '#b45309', x: 10, y: 45 },
  { id: 'raytheon', title: 'RAYTHEON ML RESEARCH', category: 'DEFENSE AI', tier: 'CORE', brilliance: 94, color: '#ea580c', x: 90, y: 45 },
  { id: 'catnet', title: 'CATNET', category: 'EDGE ML', tier: 'CORE', brilliance: 92, color: '#65a30d', x: 10, y: 65 },
  { id: 'shuttle', title: 'MDAS.ai SHUTTLE', category: 'AUTONOMY', tier: 'CORE', brilliance: 93, color: '#ca8a04', x: 90, y: 65 },
  { id: 'wgan', title: 'WGAN-GP', category: 'GENERATIVE AI', tier: 'FOUNDATION', brilliance: 88, color: '#db2777', x: 15, y: 85 },
  { id: 'mips', title: 'MIPS32 PROCESSOR', category: 'HARDWARE', tier: 'FOUNDATION', brilliance: 86, color: '#7c3aed', x: 85, y: 85 },
  { id: 'umich', title: 'BSE WITH DISTINCTION', category: 'ACADEMIC', tier: 'FOUNDATION', brilliance: 85, color: '#2563eb', x: 50, y: 95 },
];

const CONNECTIONS: [string, string][] = [
  ['topology', 'compiler'], ['topology', 'vhdl'],
  ['compiler', 'purdue'], ['purdue', 'catnet'], ['catnet', 'wgan'], ['wgan', 'umich'],
  ['vhdl', 'raytheon'], ['raytheon', 'shuttle'], ['shuttle', 'mips'], ['mips', 'umich'],
  ['compiler', 'vhdl'], ['wgan', 'mips'],
];

// -----------------------------------------------------------------------------
// SUB-COMPONENTS
// -----------------------------------------------------------------------------

const NodeShape = React.memo(({ tier, color, isHovered }: { tier: string; color: string; isHovered: boolean }) => {
  const baseSize = 12;
  const size = isHovered ? baseSize + 4 : baseSize;
  
  const commonStyle: React.CSSProperties = {
    filter: `drop-shadow(0 0 ${isHovered ? 6 : 2}px ${color})`, 
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  if (tier === 'APEX') {
    return (
      <div style={{
        ...commonStyle,
        width: 0, height: 0,
        borderLeft: `${size * 0.7}px solid transparent`,
        borderRight: `${size * 0.7}px solid transparent`,
        borderBottom: `${size * 1.2}px solid ${color}`,
      }} />
    );
  }
  
  if (tier === 'CORE') {
    return (
      <div style={{
        ...commonStyle,
        width: size, height: size,
        backgroundColor: color,
        transform: `rotate(45deg) scale(${isHovered ? 1.1 : 1})`,
        boxShadow: `0 0 ${isHovered ? 10 : 4}px ${color}`,
      }} />
    );
  }

  return (
    <div style={{
      ...commonStyle,
      width: size, height: size,
      borderRadius: '2px',
      backgroundColor: color,
      boxShadow: `0 0 ${isHovered ? 10 : 4}px ${color}`,
    }} />
  );
});

// -----------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------

const LandingPage: React.FC<LandingPageProps> = ({ setActiveOverlay }) => {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Check for mobile on mount and resize
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const particles = useMemo(() => Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: Math.random() * 2 + 1,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * -20,
    color: Math.random() > 0.5 ? '#0891b2' : '#c026d3' 
  })), []);

  const navItems: { title: string; subtitle: string; color: string; icon: typeof Globe; overlay: ActiveOverlay }[] = [
    { title: 'ECOSYSTEM', subtitle: 'Domain Architecture', color: '#ff00ff', icon: Globe, overlay: 'ecosystem' },
    { title: 'PROJECTS', subtitle: 'Showcase & Docs', color: '#00ffff', icon: Cpu, overlay: 'gallery' },
    { title: 'TERMINAL', subtitle: 'Resume / CV', color: '#00ffcc', icon: Terminal, overlay: 'terminal' },
  ];

  return (
    <div className="landing-topology">
      {/* CSS Animations */}
      <style>{`
        .landing-topology {
          position: fixed;
          top: 40px;
          left: 0;
          right: 0;
          bottom: 52px;
          background: #f8fafc;
          overflow: hidden;
          font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
        }
        @keyframes float-particle {
          0% { transform: translateY(0px) translateX(0px); opacity: 0; }
          20% { opacity: 0.4; }
          50% { transform: translateY(-100px) translateX(20px); opacity: 0.2; }
          80% { opacity: 0.4; }
          100% { transform: translateY(-200px) translateX(-10px); opacity: 0; }
        }
        .animate-float {
          animation-name: float-particle;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .nav-btn:hover { background: rgba(255,255,255,0.1) !important; }
      `}</style>

      {/* 1. BACKGROUND LAYERS */}
      
      {/* Grid */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.1,
          pointerEvents: 'none',
          backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(circle at center, black 40%, transparent 90%)',
          WebkitMaskImage: 'radial-gradient(circle at center, black 40%, transparent 90%)'
        }}
      />

      {/* Ambient Glows */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', mixBlendMode: 'multiply', opacity: 0.5 }}>
        <div style={{ position: 'absolute', top: '-10%', left: '-10%', width: '40vw', height: '40vw', background: '#f5d0fe', filter: 'blur(120px)', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', top: '-10%', right: '-10%', width: '40vw', height: '40vw', background: '#a7f3d0', filter: 'blur(120px)', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', bottom: '-10%', left: '20%', width: '60vw', height: '40vw', background: '#bfdbfe', filter: 'blur(120px)', borderRadius: '50%' }} />
      </div>

      {/* Particles */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {particles.map(p => (
          <div
            key={p.id}
            className="animate-float"
            style={{
              position: 'absolute',
              left: p.left,
              top: p.top,
              width: `${p.size}px`,
              height: `${p.size}px`,
              backgroundColor: p.color,
              borderRadius: '50%',
              animationDuration: `${p.duration}s`,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}
      </div>

      {/* 2. SVG CONNECTIONS */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10 }}>
        <defs>
          <linearGradient id="lineGradV" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0891b2" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#c026d3" stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="lineGradH" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#c026d3" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#0891b2" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#059669" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {CONNECTIONS.map(([from, to]) => {
          const fromNode = BRILLIANT_MOMENTS.find(m => m.id === from);
          const toNode = BRILLIANT_MOMENTS.find(m => m.id === to);
          if (!fromNode || !toNode) return null;

          const isHighlighted = hoveredNode === from || hoveredNode === to;
          const isDimmed = hoveredNode && !isHighlighted;
          const isHorizontal = Math.abs(fromNode.y - toNode.y) < 10;
          
          return (
            <line
              key={`${from}-${to}`}
              x1={`${fromNode.x}%`}
              y1={`${fromNode.y}%`}
              x2={`${toNode.x}%`}
              y2={`${toNode.y}%`}
              stroke={isHighlighted ? '#1e293b' : (isHorizontal ? 'url(#lineGradH)' : 'url(#lineGradV)')}
              strokeWidth={isHighlighted ? 2 : 1}
              strokeDasharray={isHighlighted ? "0" : "4 4"}
              style={{
                opacity: isHighlighted ? 1 : (isDimmed ? 0.05 : 0.4),
                transition: 'all 0.5s ease-out',
              }}
            />
          );
        })}
      </svg>

      {/* 3. INTERACTIVE NODES - Desktop: hover, Mobile: tap */}
      {!isMobile && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 20 }}>
          {BRILLIANT_MOMENTS.map((moment, index) => {
            const isHovered = hoveredNode === moment.id;
            const isDimmed = hoveredNode && !isHovered;
            
            const isLeft = moment.x < 40;
            const isRight = moment.x > 60;
            const isCenter = !isLeft && !isRight;

            return (
              <div
                key={moment.id}
                style={{
                  position: 'absolute',
                  left: `${moment.x}%`,
                  top: `${moment.y}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: isHovered ? 30 : 20,
                  opacity: mounted ? (isDimmed ? 0.2 : 1) : 0,
                  transition: 'all 0.5s ease-out',
                  transitionDelay: `${index * 50}ms`,
                  cursor: 'default',
                }}
                onMouseEnter={() => setHoveredNode(moment.id)}
                onMouseLeave={() => setHoveredNode(null)}
              >
                {/* Pulsing Aura */}
                <div
                  style={{
                    position: 'absolute',
                    width: isHovered ? 80 : 40,
                    height: isHovered ? 80 : 40,
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: moment.color,
                    opacity: isHovered ? 0.15 : 0,
                    filter: 'blur(16px)',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                    transition: 'all 0.4s',
                  }}
                />
                
                {/* Content Container */}
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexDirection: isCenter ? 'column' : isLeft ? 'row' : 'row-reverse',
                  textAlign: isCenter ? 'center' : 'left',
                }}>
                  <NodeShape tier={moment.tier} color={moment.color} isHovered={isHovered} />
                  
                  {/* Label Card - Shows on hover */}
                  <div 
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: `1px solid ${isHovered ? `${moment.color}60` : 'rgba(0,0,0,0.08)'}`,
                      backgroundColor: isHovered ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.6)',
                      backdropFilter: 'blur(8px)',
                      boxShadow: isHovered ? `0 4px 16px -4px ${moment.color}40` : 'none',
                      marginTop: isCenter ? '8px' : '0',
                      opacity: isHovered ? 1 : 0.7,
                      transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                      transition: 'all 0.3s ease-out',
                      pointerEvents: 'none',
                    }}
                  >
                    <div style={{ 
                      color: moment.color, 
                      fontWeight: 700, 
                      fontSize: '10px', 
                      letterSpacing: '0.08em', 
                      marginBottom: '2px', 
                      whiteSpace: 'nowrap' 
                    }}>
                      {moment.title}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: isCenter ? 'center' : 'flex-start',
                      gap: '8px',
                      fontSize: '9px', 
                    }}>
                      <span style={{ color: '#6b7280', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {moment.category}
                      </span>
                      {isHovered && (
                        <span style={{ 
                          color: moment.color, 
                          fontWeight: 700,
                          fontFamily: 'monospace',
                        }}>
                          {moment.brilliance}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MOBILE: Compact highlights strip */}
      {isMobile && (
        <div style={{
          position: 'absolute',
          bottom: 8,
          left: 0,
          right: 0,
          zIndex: 40,
          padding: '0 12px',
          pointerEvents: 'auto',
        }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
            paddingBottom: '4px',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
          }}>
            {BRILLIANT_MOMENTS.slice(0, 6).map((moment) => (
              <div
                key={moment.id}
                style={{
                  flex: '0 0 auto',
                  width: '100px',
                  padding: '8px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255,255,255,0.85)',
                  borderLeft: `3px solid ${moment.color}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  scrollSnapAlign: 'start',
                }}
              >
                <div style={{ 
                  fontSize: '9px', 
                  fontWeight: 700, 
                  color: moment.color,
                  letterSpacing: '0.05em',
                  marginBottom: '4px',
                }}>
                  {moment.tier}
                </div>
                <div style={{ 
                  fontSize: '10px', 
                  fontWeight: 600, 
                  color: '#1e293b',
                  lineHeight: 1.2,
                  marginBottom: '2px',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}>
                  {moment.title}
                </div>
                <div style={{ 
                  fontSize: '8px', 
                  color: '#64748b',
                  fontFamily: 'monospace',
                }}>
                  {moment.brilliance}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 4. CENTER HERO CARD - DARK THEME */}
      <div style={{ 
        position: 'absolute', 
        inset: 0, 
        zIndex: 30, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: isMobile ? 'flex-start' : 'center', 
        padding: isMobile ? '24px 16px' : '16px', 
        paddingTop: isMobile ? '20px' : '16px',
        pointerEvents: 'none' 
      }}>
        <div 
          style={{
            pointerEvents: 'auto',
            maxWidth: isMobile ? '100%' : '512px',
            width: '100%',
            padding: '4px',
            borderRadius: '16px',
            background: 'linear-gradient(145deg, rgba(0,0,0,0.1), transparent)',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s',
          }}
        >
          <div style={{
            background: 'rgba(10,10,14,0.95)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: isMobile ? '20px 16px' : '32px',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            position: 'relative',
            overflow: 'hidden',
            color: '#e2e8f0',
          }}>
            {/* Top highlight */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(to right, transparent, rgba(6,182,212,0.5), transparent)' }} />
            
            <div style={{ textAlign: 'center', position: 'relative', zIndex: 10 }}>
              <h1 style={{ fontSize: 'clamp(1.5rem, 5vw, 3rem)', fontWeight: 700, letterSpacing: '-0.025em', marginBottom: '24px', color: 'white' }}>
                <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#ec4899', animation: 'pulse 2s infinite' }}>_</span>
                  <span style={{ background: 'linear-gradient(to right, white, #e5e7eb, #9ca3af)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    RODERICK
                  </span>
                </span>
                <span style={{ color: '#22d3ee' }}>.</span>
                <span style={{ background: 'linear-gradient(to right, #22d3ee, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  RENWICK
                </span>
              </h1>
              
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                justifyContent: 'center', 
                gap: isMobile ? '8px 12px' : '16px 16px', 
                fontSize: isMobile ? '10px' : '12px', 
                color: '#9ca3af', 
                fontWeight: 300, 
                letterSpacing: '0.1em', 
                marginBottom: isMobile ? '20px' : '32px' 
              }}>
                <span>AI SYSTEMS ENGINEER</span>
                <span style={{ color: '#0891b2' }}>//</span>
                <span>ML RESEARCHER</span>
                <span style={{ color: '#0891b2' }}>//</span>
                <span>PURDUE MSECE</span>
              </div>

              {/* Navigation Buttons */}
              <div style={{ display: 'grid', gap: isMobile ? '10px' : '12px', maxWidth: '320px', margin: '0 auto' }}>
                {navItems.map((btn, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveOverlay(btn.overlay)}
                    className="nav-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: isMobile ? '16px 18px' : '14px 20px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      minHeight: isMobile ? '56px' : 'auto', // Touch-friendly height
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = `${btn.color}50`;
                      e.currentTarget.style.boxShadow = `0 0 15px ${btn.color}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <btn.icon size={16} style={{ color: btn.color }} />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', color: '#e5e7eb' }}>
                          {btn.title}
                        </div>
                        <div style={{ fontSize: '10px', color: '#6b7280' }}>
                          {btn.subtitle}
                        </div>
                      </div>
                    </div>
                    <ChevronRight size={14} style={{ color: '#4b5563' }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default LandingPage;
